import { DataTableVisual } from './data-table-visual';
import { DataTableColumn, CellOverflowProps } from './data-table.types';

/** Simple column definition for server-side tables */
export interface ServerDataTableColumnDef<TData> {
  /** Unique column identifier */
  id: string;
  /** Column header content */
  header: React.ReactNode;
  /** Render function for cell content */
  cell: (row: TData) => React.ReactNode;
  /** Whether this column is sortable */
  sortable?: boolean;
  /** Column width */
  width?: string | number;
  /** Minimum column width */
  minWidth?: string | number;
  /** Maximum column width */
  maxWidth?: string | number;
  /** Pin column to left or right */
  pinned?: 'left' | 'right';
}

/** Sorting state for server-side operations */
export interface ServerSortingState {
  id: string;
  desc: boolean;
}

export interface ServerDataTableProps<TData> extends CellOverflowProps {
  /** Column definitions (simple format, not TanStack) */
  columns: ServerDataTableColumnDef<TData>[];
  /** Current page data from server */
  data: TData[];

  // Visual styling
  /** Visual appearance style */
  appearance?: 'default' | 'minimal';

  // Pagination (server-controlled)
  /** Current page number (1-indexed) */
  page: number;
  /** Total number of pages */
  pageCount: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;

  // Sorting (server-controlled)
  /** Current sorting state */
  sorting: ServerSortingState[];
  /** Callback when sorting changes */
  onSortingChange: (sorting: ServerSortingState[]) => void;

  // Search (optional, server-controlled)
  /** Enable search input */
  enableSearch?: boolean;
  /** Current search value */
  searchValue?: string;
  /** Callback when search changes */
  onSearchChange?: (value: string) => void;
  /** Search placeholder text */
  searchPlaceholder?: string;

  // Scrolling
  /** Enable vertical scrolling with fixed height */
  scrollable?: boolean;
  /** Height for scrollable container */
  height?: string;
  /** Enable horizontal scrolling */
  horizontalScroll?: boolean;

  // States
  /** Show loading overlay */
  loading?: boolean;
  /** Loading message text */
  loadingText?: string;
  /** Empty state message */
  emptyText?: string;

  /** Additional CSS classes */
  className?: string;
}

/**
 * ServerDataTable - Server-side pagination and sorting
 *
 * Parent component fetches data from server and controls pagination/sorting.
 *
 * @example
 * ```tsx
 * const [page, setPage] = useState(1);
 * const [sorting, setSorting] = useState([{ id: 'name', desc: false }]);
 * const { data, loading } = useServerData({ page, sorting });
 *
 * <ServerDataTable
 *   columns={columns}
 *   data={data?.results ?? []}
 *   loading={loading}
 *   page={page}
 *   pageCount={data?.pagination.total_pages ?? 0}
 *   onPageChange={setPage}
 *   sorting={sorting}
 *   onSortingChange={setSorting}
 * />
 * ```
 *
 * @internal - Use DataTable with variant="server" instead
 */
export function ServerDataTable<TData>({
  columns,
  data,
  appearance = 'minimal',
  page,
  pageCount,
  onPageChange,
  sorting,
  onSortingChange,
  enableSearch = false,
  searchValue = '',
  onSearchChange,
  searchPlaceholder,
  scrollable = false,
  height,
  horizontalScroll = false,
  cellOverflow,
  maxLines,
  loading = false,
  loadingText,
  emptyText,
  className,
}: ServerDataTableProps<TData>) {
  // Convert simple columns to DataTable visual format
  const visualColumns: DataTableColumn<TData>[] = columns.map((col) => ({
    id: col.id,
    header: col.header,
    cell: (row) => col.cell(row),
    sortable: col.sortable,
    width: col.width,
    minWidth: col.minWidth,
    maxWidth: col.maxWidth,
    pinned: col.pinned,
  }));

  // Handle sorting
  const handleSort = (columnId: string) => {
    const currentSort = sorting[0];
    if (currentSort?.id === columnId) {
      onSortingChange([{ id: columnId, desc: !currentSort.desc }]);
    } else {
      onSortingChange([{ id: columnId, desc: false }]);
    }
  };

  return (
    <DataTableVisual
      columns={visualColumns}
      rows={data}
      variant={appearance}
      enableSearch={enableSearch}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder={searchPlaceholder}
      sortedColumn={sorting[0]?.id}
      sortDirection={sorting[0]?.desc ? 'desc' : 'asc'}
      onSort={handleSort}
      enablePagination={pageCount > 1}
      currentPage={page}
      totalPages={pageCount}
      onPageChange={onPageChange}
      scrollable={scrollable}
      height={height}
      horizontalScroll={horizontalScroll}
      loading={loading}
      loadingText={loadingText}
      cellOverflow={cellOverflow}
      maxLines={maxLines}
      emptyText={emptyText}
      className={className}
    />
  );
}
